import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { Messages } from 'primereact/messages';
import classNames from 'classnames';

import { Header, SideNav, PageActionsContext } from 'components';
import { buildNavItems, buildDocsNavItems } from 'components/Header/navigation';
import { useUserQuery } from 'modules/App/queries';

import {
  routes,
  resolveActiveProduct,
  resolvePageTitle,
  resolvePageDescription,
  resolvePageSubtitle,
} from 'constants/index';

import './navigation-layout.scss';

/*
 * The sidebar runs the full height on the left. Everything else - the header,
 * the page head and the page itself - stacks in the column beside it. On UAT
 * and locally a banner spans the top of both.
 */
export default function NavigationLayout() {
  const messagesRef = useRef<Messages>(null);
  const { pathname } = useLocation();
  const { ticketId, reference } = useParams();
  const userQuery = useUserQuery();

  const isRolePage = pathname.includes(routes.role);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);

  const userType = userQuery.data?.data.data.user.userTypes?.find(
    ({ id }) => id === userQuery.data?.data.data.user.userTypeId
  );

  const isDocs = pathname.startsWith(routes.docs.index);

  const navItems = isDocs
    ? buildDocsNavItems()
    : buildNavItems(resolveActiveProduct(pathname).key, userType?.menu);
  const pageTitle = resolvePageTitle({ pathname, ticketId, reference });
  const pageDescription = resolvePageDescription({
    pathname,
    ticketId,
    reference,
  });
  const pageSubtitle = resolvePageSubtitle({ pathname, ticketId, reference });
  // The dashboards go without a title: the sidebar already says where you
  // are and their cards carry their own headings.
  const isPageHeaderVisible =
    !isRolePage &&
    !pathname.includes(routes.authentication.resetPin) &&
    pathname !== routes.main &&
    pathname !== routes.overview;

  const isDevelopment =
    window.location.hostname.includes('uat') ||
    window.location.hostname.includes('localhost');

  useEffect(() => {
    if (isDevelopment)
      messagesRef.current?.show({
        sticky: true,
        severity: 'warn',
        detail: 'Development Mode',
        closable: false,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div
      className={classNames('navigation-layout', {
        'navigation-layout--bare': isRolePage,
        'navigation-layout--banner': isDevelopment,
      })}
    >
      <Messages ref={messagesRef} />

      <div className="navigation-layout__frame">
        {!isRolePage && (
          <SideNav
            items={navItems}
            isDrawerOpen={isDrawerOpen}
            onDrawerClose={() => setDrawerOpen(false)}
          />
        )}

        <div className="navigation-layout__column">
          <header className="navigation-layout-header">
            <Header
              isRolePage={isRolePage}
              onMenuOpen={() => setDrawerOpen(true)}
            />
          </header>

          {isPageHeaderVisible && (
            <div className="navigation-layout__page-head">
              <div className="navigation-layout__title-row">
                <div>
                  <h1 className="navigation-layout__title">{pageTitle}</h1>
                  {pageSubtitle && (
                    <p className="navigation-layout__subtitle">
                      {pageSubtitle}
                    </p>
                  )}
                  {pageDescription && (
                    <p className="navigation-layout__description">
                      {pageDescription}
                    </p>
                  )}
                </div>

                <div
                  className="navigation-layout__actions"
                  ref={setActionsSlot}
                />
              </div>
            </div>
          )}

          <main className="navigation-layout__main">
            <PageActionsContext.Provider
              value={isPageHeaderVisible ? actionsSlot : undefined}
            >
              <Outlet />
            </PageActionsContext.Provider>
          </main>
        </div>
      </div>
    </div>
  );
}
